import {
	type InputLabelProps as MuiInputLabelProps,
	type InputProps as MuiInputProps,
	Stack,
	type SxProps,
	TextField,
	type TextFieldProps,
	type Theme,
	Typography,
	useTheme,
} from "@mui/material";
import type { ChangeEvent, FC } from "react";

export type MyPlannInputProps = Omit<
	TextFieldProps,
	"InputProps" | "InputLabelProps"
> & {
	title?: string;
	fullWidth?: boolean;
	autocomplete?: string;
	inputRef?: React.Ref<HTMLInputElement>;
	/**
	 * @deprecated Use `slotProps.input` instead. This prop is maintained for backward compatibility.
	 */
	InputProps?: Partial<MuiInputProps>;
	/**
	 * @deprecated Use `slotProps.inputLabel` instead. This prop is maintained for backward compatibility.
	 */
	InputLabelProps?: Partial<MuiInputLabelProps>;
};

export const MyPlannInput: FC<MyPlannInputProps> = ({
	value,
	InputLabelProps,
	InputProps,
	slotProps,
	title,
	fullWidth,
	autocomplete,
	inputRef,
	...rest
}) => {
	const theme = useTheme();

	const inputProps = ((slotProps?.input && typeof slotProps.input === "object"
		? slotProps.input
		: undefined) || InputProps) as Partial<MuiInputProps> | undefined;
	const hasStartAdornment = Boolean(inputProps?.startAdornment);

	const inputLabelProps = ((slotProps?.inputLabel &&
	typeof slotProps.inputLabel === "object"
		? slotProps.inputLabel
		: undefined) || InputLabelProps) as Partial<MuiInputLabelProps> | undefined;

	const baseLabelSx = Array.isArray(inputLabelProps?.sx)
		? inputLabelProps.sx.reduce(
				(merged, style) => Object.assign(merged, style),
				{},
			)
		: inputLabelProps?.sx;

	const labelSx: SxProps<Theme> = {
		...baseLabelSx,
		"&.MuiInputLabel-shrink": {
			transform: hasStartAdornment
				? "translate(4em, 0.5em) scale(1)"
				: "translate(12px, 8px) scale(1)",
		},
	};

	const inputSx: SxProps<Theme> = {
		"& .MuiOutlinedInput-input": {
			"&:not(:placeholder-shown)": {
				paddingTop: hasStartAdornment
					? rest.label
						? "14px"
						: "0px"
					: rest.label
						? "20px"
						: "0px",
				paddingLeft: hasStartAdornment ? "0.9em" : "0px",
			},
			padding: 0,
			"&.Mui-disabled": {
				WebkitTextFillColor: theme.palette.deprecated.grey[700],
			},
		},
		"& .MuiOutlinedInput-root": {
			"&.Mui-disabled": {
				border: "none",
				backgroundColor: (theme) => theme.palette.deprecated.grey[50],
			},
		},
	};

	const isNumericValue = typeof value === "number";
	const displayValue = isNumericValue
		? (value as number).toLocaleString("cs-CZ")
		: (value ?? "");

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (isNumericValue && rest.onChange) {
			const input = e.currentTarget;
			const raw = input.value.replace(/[\u00A0\s]/g, "").replace(",", ".");
			input.value = raw;
			rest.onChange(e);
		} else {
			rest.onChange?.(e);
		}
	};

	return (
		<Stack width={fullWidth ? "100%" : undefined}>
			{title && (
				<Typography
					variant="deprecatedSubtitle1"
					color={theme.palette.deprecated.grey[500]}
					pb={1.2}
				>
					{title}
				</Typography>
			)}
			<TextField
				{...rest}
				onChange={handleChange}
				value={displayValue}
				slotProps={{
					input: inputProps,
					inputLabel: {
						...inputLabelProps,
						sx: labelSx,
						shrink: Boolean(value) || inputLabelProps?.shrink,
					},
				}}
				sx={inputSx}
				inputProps={{
					...rest.inputProps,
					autoComplete: autocomplete,
					ref: inputRef,
				}}
			/>
		</Stack>
	);
};
